import './tailwind.css';
import './variables.css';
import './index.css';
import './fonts.css';

// mock data
import './mock';

import { Provider } from 'react-redux';
import store from './redux/store';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { NotFoundPage } from './pages/404';
import { Loading } from './components/loading';
import { Unauthorized } from './pages/unauthorized';
import { GeneralPage } from './pages/general';
import { AllWorkOrdersPage } from './pages/all-work-orders';
import { ConfigProvider, ThemeConfig } from 'antd';
import { LoginPage } from './pages/auth/login';
import { AuthCallbackPage } from './pages/auth/callback';
import { AuthGuard } from './auth/AuthGuard';
import { OwnerGuard } from './auth/OwnerGuard';
import { LayoutType } from './utils/enums';
import { StartNewWorkOrderPage } from './pages/start-work-order';
import { CarsPage } from './pages/cars';
import { ServicesPage } from './pages/services';
import { UserServicesPage } from './pages/user-service';
import { ShopsPage } from './pages/shops';
import { RegisterPage } from './pages/auth/register';
import { registerWorker } from './firebase/registerWorker';
import { AddReceiptsPage } from './pages/add-receipts';
import { ChangePasswordPage } from './pages/auth/change-password';
import { ReportPage } from './pages/report';
import { MyWorkOrdersPage } from './pages/work-order';
import { TuroReservationsPage } from './pages/turo-reservations';
import { ReservationCalendarPage } from './pages/reservation-calendar';
import { BillingPage } from './pages/billing';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { keycloak, keycloakInitOptions } from './auth/keycloak';

const config: ThemeConfig = {
    hashed: false,
    token: {
        fontFamily: 'Roboto, sans-serif',
        colorPrimary: '#7357FF',
    },
};

const router = createBrowserRouter([
    {
        // Root is the login page itself. Previously `/` redirected to
        // `/work-order`, which then triggered the AuthGuard's
        // auto-login redirect (keycloak.login with idpHint=google) on
        // every visit. That meant typing the bare domain would
        // immediately bounce to Google's account picker — a jarring
        // experience, and the cause of the /work-order loop the user
        // reported: every re-render re-evaluated the AuthGuard.
        //
        // Now the user lands on the LoginPage and explicitly clicks
        // "Continue with Google" to authenticate. The LoginPage
        // already auto-navigates to /work-order when it detects an
        // existing Keycloak session, so this preserves the post-login
        // flow.
        path: '/',
        element: <Unauthorized page={<LoginPage />} />,
    },
    {
        path: '/login',
        element: <Unauthorized page={<LoginPage />} />,
    },
    {
        path: '/register',
        element: <Unauthorized page={<RegisterPage />} />,
    },
    {
        path: '/change-password/:email',
        element: <Unauthorized page={<ChangePasswordPage />} />,
    },
    {
        path: '/all-work-orders',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<AllWorkOrdersPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'All Work Orders',
                description: 'Welcome to the All Work Orders page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/work-order',
        element: (
            <AuthGuard>
                <GeneralPage
                    layout={LayoutType.ADMIN}
                    page={<MyWorkOrdersPage />}
                    isPrivate
                />
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'My Work Orders',
                description: 'Welcome to the My Work Orders page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/start-work-order',
        element: (
            <AuthGuard>
                <GeneralPage
                    layout={LayoutType.ADMIN}
                    page={<StartNewWorkOrderPage />}
                    isPrivate
                />
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'New Work Order',
                description: 'Welcome to the work order page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/add-receipts',
        element: (
            <AuthGuard>
                <GeneralPage
                    layout={LayoutType.ADMIN}
                    page={<AddReceiptsPage />}
                    isPrivate
                />
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Add Receipt',
                description: 'Welcome to the Add Receipt page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/settings/shops',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<ShopsPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Shops',
                description: 'Third-party vendor shops',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/settings/cars',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<CarsPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Cars',
                description: 'Welcome to the Cars page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/settings/services',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<ServicesPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Services',
                description: 'Welcome to the Services page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/settings/user-service',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<UserServicesPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Users',
                description: 'Welcome to the User Services page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/settings/turo-reservations',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<TuroReservationsPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Import Reservation',
                description:
                    'View and manage Turo trip reservations and earnings',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/reservation-calendar',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<ReservationCalendarPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Reservation Calendar',
                description: 'View vehicle booking calendar',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        path: '/billing',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<BillingPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Billing',
                description: 'Manage your subscription',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    // report
    {
        path: '/report',
        element: (
            <AuthGuard>
                <OwnerGuard>
                    <GeneralPage
                        layout={LayoutType.ADMIN}
                        page={<ReportPage />}
                        isPrivate
                    />
                </OwnerGuard>
            </AuthGuard>
        ),
        handle: {
            info: {
                title: 'Report',
                description: 'Welcome to the Report page',
            },
            crumbs: [
                {
                    title: 'Home',
                    href: '/',
                },
            ],
        },
    },
    {
        // Post-Keycloak-redirect landing page. Keycloak sends users back
        // here with the auth code in the URL fragment; the page hydrates
        // Redux from the token claims, POSTs /api/users/sync, and
        // navigates onward. Not auth-guarded (it IS the auth flow).
        path: '/auth/callback',
        element: <AuthCallbackPage />,
    },
    {
        path: '*',
        element: <NotFoundPage />,
    },
]);

const Root: React.FC = () => {
    return (
        <ConfigProvider theme={config}>
            <Provider store={store}>
                <div className="App">
                    <RouterProvider router={router} />
                    <Loading />
                </div>
            </Provider>
        </ConfigProvider>
    );
};

const root = createRoot(document.getElementById('root') as HTMLElement);

// Static splash shown while Keycloak initializes. We deliberately keep
// this free of any React-Redux hooks: the singleton <Provider> is only
// mounted once we render <Root /> below. Rendering <Loading /> here would
// call useSelector() outside the Provider tree, which react-redux v8
// surfaces as `Cannot read properties of null (reading 'store')` at the
// context-consumer boundary (`null.store`). We hit this intermittently
// on social-login round-trips (Facebook IdP adds an extra OAuth hop vs
// Google, making the race more visible) — the static splash dodges it.
//
// We also include a small "Loading…" label so the user sees feedback
// during the 10s silent-SSO check. A bare spinner on a purple background
// is easy to mistake for an empty/broken page.
const BootstrapSplash: React.FC = () => {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                background: '#7357ff',
                color: '#ffffff',
                fontFamily:
                    'Roboto, system-ui, -apple-system, "Segoe UI", sans-serif',
            }}
        >
            <div
                style={{
                    width: 48,
                    height: 48,
                    border: '4px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#ffffff',
                    borderRadius: '50%',
                    animation: 'autotrack-spin 0.9s linear infinite',
                }}
            />
            <div style={{ fontSize: 14, opacity: 0.9, letterSpacing: 0.4 }}>
                Loading Auto Track…
            </div>
            <style>
                {
                    '@keyframes autotrack-spin { to { transform: rotate(360deg); } }'
                }
            </style>
        </div>
    );
};

// Keycloak must finish initializing before the React tree mounts so that
// `keycloak.authenticated` is accurate on first render and `keycloak.token`
// is available to the ApiInstance interceptor from the very first request.
//
// Hard safety timeout: even if `keycloak.init()` somehow never resolves
// or rejects (e.g. browser blocks the silent-SSO iframe indefinitely
// due to third-party cookie restrictions, and the internal
// `messageReceiveTimeout` race never fires), we still mount the real
// React tree after 15 s. Without this guard the user would be stuck on
// the splash forever and see an empty purple page.
const KEYCLOAK_INIT_HARD_TIMEOUT_MS = 15000;

async function bootstrap(): Promise<void> {
    // Use a static splash (no Redux hooks) until Keycloak init resolves,
    // then mount the full <Root /> tree that includes <Provider store>.
    // See BootstrapSplash comment for why we don't render <Loading /> here.
    root.render(<BootstrapSplash />);

    const initPromise = keycloak.init(keycloakInitOptions).catch(err => {
        // eslint-disable-next-line no-console
        console.error('[keycloak] init failed', err);
    });

    let hardTimer: ReturnType<typeof setTimeout> | undefined;
    const hardTimeout = new Promise<void>(resolve => {
        hardTimer = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn(
                `[keycloak] init did not settle within ${KEYCLOAK_INIT_HARD_TIMEOUT_MS}ms; mounting app anyway`
            );
            resolve();
        }, KEYCLOAK_INIT_HARD_TIMEOUT_MS);
    });

    try {
        await Promise.race([initPromise, hardTimeout]);
    } finally {
        if (hardTimer) clearTimeout(hardTimer);
    }

    root.render(<Root />);
}

void bootstrap();

// register service worker
registerWorker();
